import React, { useMemo, useRef, useState } from "react";
import {
  Avatar,
  formatRecipient,
  icons,
  matchesQuery,
  sameRecipient,
  sentAgo,
  toRecipient,
} from "../utils/recipients";

/**
 * Payee Management POC (REQS-50): the "To" row with saved and recent recipients.
 *
 * Focusing the field lists favorites first, then people recently sent to, filtered by what's
 * typed. The star at the end of the field saves the current recipient under a name (or removes a
 * saved one). `recipients` is the shared `useRecipients()` result so the "Send again" strip above
 * stays in sync. Nothing about how the recipient receives money is ever shown.
 */
const MAX_SUGGESTIONS = 6;

const RecipientField = ({ recipients, value, onChange, placeholder, valid }) => {
  const { people, findSaved, save: saveRecipient, remove } = recipients;
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [nickname, setNickname] = useState("");
  const [error, setError] = useState("");
  const blurTimer = useRef(null);

  const savedMatch = findSaved(value);

  const suggestions = useMemo(
    () =>
      people
        .filter((p) => matchesQuery(p, value) && !sameRecipient(p, value))
        .slice(0, MAX_SUGGESTIONS),
    [people, value]
  );

  const pick = (entry) => {
    onChange({ target: { value: entry.value } });
    setOpen(false);
  };

  const save = async () => {
    setError("");
    try {
      await saveRecipient(toRecipient(value), nickname);
      setNaming(false);
      setNickname("");
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't save this contact.");
    }
  };

  const unsave = async () => {
    setError("");
    try {
      await remove(savedMatch.uuid);
    } catch (err) {
      setError("Couldn't remove this contact.");
    }
  };

  const showStar = valid || Boolean(savedMatch);
  const showList = open && !naming && suggestions.length > 0;

  return (
    <>
      <div className="np-row np-recipient-row">
        <label htmlFor="recipient">To</label>
        <div className="np-recipient-input">
          <input
            id="recipient"
            value={value}
            placeholder={placeholder}
            autoComplete="off"
            onChange={(e) => {
              onChange(e);
              setOpen(true);
            }}
            onFocus={() => {
              clearTimeout(blurTimer.current);
              setOpen(true);
            }}
            onBlur={() => {
              // Let a tap on a suggestion land before the list closes.
              blurTimer.current = setTimeout(() => setOpen(false), 150);
            }}
          />
          {savedMatch && <span className="np-recipient-tag">{savedMatch.nickname}</span>}
        </div>
        {showStar && (
          <button
            type="button"
            className={`np-star-btn${savedMatch ? " is-saved" : ""}`}
            aria-label={savedMatch ? `Remove ${savedMatch.nickname} from contacts` : "Save as a contact"}
            aria-pressed={Boolean(savedMatch)}
            onClick={() => (savedMatch ? unsave() : setNaming((n) => !n))}
          >
            {icons.star(Boolean(savedMatch))}
          </button>
        )}
      </div>

      {naming && !savedMatch && (
        <div className="np-row np-recipient-name-row">
          <label htmlFor="recipient_nickname">Name</label>
          <input
            id="recipient_nickname"
            autoFocus
            placeholder="e.g. Mom"
            value={nickname}
            maxLength={100}
            onChange={(e) => setNickname(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && nickname.trim()) {
                e.preventDefault();
                save();
              }
              if (e.key === "Escape") setNaming(false);
            }}
          />
          <button
            type="button"
            className="np-inline-action"
            disabled={!nickname.trim()}
            onClick={save}
          >
            Save
          </button>
        </div>
      )}

      {error && <div className="np-recipient-error">{error}</div>}

      {showList && (
        <ul className="np-suggestions" role="listbox" aria-label="Contacts">
          {suggestions.map((s) => (
            <li
              key={`${s.type}:${s.value}`}
              className="np-list-row np-suggestion"
              role="option"
              aria-selected="false"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(s)}
            >
              <Avatar entry={s} />
              <div className="np-list-body">
                <div className="np-list-title">
                  {s.saved ? s.nickname : formatRecipient(s)}
                </div>
                <div className="np-list-sub">
                  {s.saved ? formatRecipient(s) : sentAgo(s.last_sent_at)}
                </div>
              </div>
              {s.saved && <span className="np-suggestion-star">{icons.star(true)}</span>}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default RecipientField;
